import React from 'react';
import { ArrowLeft, Check, Clock, Download, Mail, Copy, Building2, Calendar, Users } from 'lucide-react';
interface QuoteDetailProps {
  quoteId: string;
  onBack: () => void;
  quotes: any[];
}
export function QuoteDetail({
  quoteId,
  onBack,
  quotes
}: QuoteDetailProps) {
  const quote = quotes.find(q => q.id === quoteId);
  if (!quote) return <div data-id="element-908">Quote not found</div>;
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Fully Booked':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200 font-bold';
      case 'Contract Issued':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Deposit Pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Hold Placed':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Viewed':
        return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'Draft':
      case 'Submitted':
      case 'Pending Review':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      case 'Rejected':
      case 'Lost':
      case 'Expired':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-909">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-910">
        <div className="flex items-center gap-4" data-id="element-911">
          <button onClick={onBack} className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-200 rounded-full transition-colors" data-id="element-912">
            <ArrowLeft className="w-5 h-5" data-id="element-913" />
          </button>
          <div data-id="element-914">
            <div className="flex items-center gap-3 mb-1" data-id="element-915">
              <h1 className="font-serif text-3xl text-stone-900" data-id="element-916">
                {quote.groupName}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(quote.status)}`} data-id="element-917">
                {quote.status}
              </span>
            </div>
            <p className="text-stone-500 font-mono text-sm" data-id="element-918">
              {quote.id} • Prepared for {quote.agency}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3" data-id="element-919">
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-white border border-stone-200 hover:bg-stone-50 transition-colors shadow-sm" data-id="element-920">
            <Download className="w-4 h-4" data-id="element-921" />
            PDF
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 transition-colors shadow-sm" data-id="element-922">
            <Mail className="w-4 h-4" data-id="element-923" />
            Send to Agency
          </button>
        </div>
      </div>

      {/* Key Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-924">
        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-925">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-926">
            <Building2 className="w-5 h-5" data-id="element-927" />
          </div>
          <div data-id="element-928">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-929">
              Property
            </p>
            <p className="font-medium text-stone-900" data-id="element-930">{quote.property}</p>
            <p className="text-sm text-stone-500" data-id="element-931">All-Inclusive Resort</p>
          </div>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-932">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-933">
            <Calendar className="w-5 h-5" data-id="element-934" />
          </div>
          <div data-id="element-935">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-936">
              Dates
            </p>
            <p className="font-medium text-stone-900" data-id="element-937">{quote.dates}</p>
            <p className="text-sm text-stone-500" data-id="element-938">4 Nights</p>
          </div>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm flex items-start gap-4" data-id="element-939">
          <div className="p-3 bg-stone-100 rounded-md text-stone-600" data-id="element-940">
            <Users className="w-5 h-5" data-id="element-941" />
          </div>
          <div data-id="element-942">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-943">
              Inventory
            </p>
            <p className="font-medium text-stone-900" data-id="element-944">{quote.rooms} Rooms</p>
            <p className="text-sm text-stone-500" data-id="element-945">~{quote.rooms * 2} Guests</p>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div data-id="element-946">
        <h2 className="font-serif text-2xl text-stone-900 mb-6 mt-8" data-id="element-947">
          Auto-Generated Packages
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch" data-id="element-948">
          {/* Good */}
          <div className="bg-white border border-stone-200 shadow-sm flex flex-col" data-id="element-949">
            <div className="p-6 border-b border-stone-200 bg-stone-50/50" data-id="element-950">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-951">
                Option 1
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-952">
                Standard Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-953">
                <span className="font-mono text-3xl text-stone-900" data-id="element-954">
                  ${(quote.value * 0.85).toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-955">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-956">
                ${Math.round(quote.value * 0.85 / quote.rooms / 4)} / room /
                night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-957">
              <ul className="space-y-4" data-id="element-958">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-959">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-960" />
                  <span data-id="element-961">Run of House Rooms</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-962">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-963" />
                  <span data-id="element-964">Standard All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-965">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-966" />
                  <span data-id="element-967">1 comp room per 25 booked</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-968">
              <button className="w-full py-2.5 border border-stone-300 text-stone-700 font-medium text-sm hover:bg-stone-50 transition-colors" data-id="element-969">
                Select Option
              </button>
            </div>
          </div>

          {/* Better (Recommended) */}
          <div className="bg-white border-2 border-red-600 shadow-md flex flex-col relative transform lg:-translate-y-2" data-id="element-970">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-red-600 text-white font-mono text-xs uppercase tracking-widest px-3 py-1" data-id="element-971">
              Recommended
            </div>
            <div className="p-6 border-b border-stone-200 bg-red-50/30" data-id="element-972">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-2" data-id="element-973">
                Option 2
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-974">
                Premium Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-975">
                <span className="font-mono text-3xl text-stone-900" data-id="element-976">
                  ${quote.value.toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-977">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-978">
                ${Math.round(quote.value / quote.rooms / 4)} / room / night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-979">
              <ul className="space-y-4" data-id="element-980">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-981">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-982" />
                  <span className="font-medium text-stone-900" data-id="element-983">
                    Ocean View Rooms Guaranteed
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-984">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-985" />
                  <span data-id="element-986">Standard All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-987">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-988" />
                  <span className="font-medium text-stone-900" data-id="element-989">
                    1 comp room per 20 booked
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-990">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-991" />
                  <span data-id="element-992">Private group check-in area</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-993">
                  <Check className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" data-id="element-994" />
                  <span data-id="element-995">1 hour welcome cocktail reception</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-996">
              <button className="w-full py-2.5 bg-red-600 text-white font-medium text-sm hover:bg-red-700 transition-colors shadow-sm" data-id="element-997">
                {quote.status === 'Hold Placed' ? 'Release Hold' : 'Place 72hr Hold'}
              </button>
            </div>
          </div>

          {/* Best */}
          <div className="bg-white border border-stone-200 shadow-sm flex flex-col" data-id="element-998">
            <div className="p-6 border-b border-stone-200 bg-stone-50/50" data-id="element-999">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-1000">
                Option 3
              </p>
              <h3 className="font-serif text-2xl text-stone-900 mb-4" data-id="element-1001">
                VIP Block
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-1002">
                <span className="font-mono text-3xl text-stone-900" data-id="element-1003">
                  ${(quote.value * 1.25).toLocaleString()}
                </span>
                <span className="text-stone-500 text-sm" data-id="element-1004">total</span>
              </div>
              <p className="font-mono text-sm text-stone-500 mt-1" data-id="element-1005">
                ${Math.round(quote.value * 1.25 / quote.rooms / 4)} / room /
                night
              </p>
            </div>
            <div className="p-6 flex-1" data-id="element-1006">
              <ul className="space-y-4" data-id="element-1007">
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1008">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1009" />
                  <span data-id="element-1010">Ocean Front Suites Guaranteed</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1011">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1012" />
                  <span data-id="element-1013">Premium All-Inclusive Plan</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1014">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1015" />
                  <span data-id="element-1016">1 comp room per 15 booked</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1017">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1018" />
                  <span data-id="element-1019">Private group check-in area</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1020">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1021" />
                  <span data-id="element-1022">Private farewell dinner event</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-600" data-id="element-1023">
                  <Check className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" data-id="element-1024" />
                  <span data-id="element-1025">VIP amenities in all rooms</span>
                </li>
              </ul>
            </div>
            <div className="p-6 pt-0 mt-auto" data-id="element-1026">
              <button className="w-full py-2.5 border border-stone-300 text-stone-700 font-medium text-sm hover:bg-stone-50 transition-colors" data-id="element-1027">
                Select Option
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Trail */}
      <div className="mt-12 bg-white border border-stone-200 shadow-sm" data-id="element-1028">
        <div className="p-6 border-b border-stone-200" data-id="element-1029">
          <h2 className="font-serif text-xl text-stone-900" data-id="element-1030">Audit Trail</h2>
        </div>
        <div className="p-6" data-id="element-1031">
          <div className="space-y-6" data-id="element-1032">
            <div className="flex gap-4" data-id="element-1033">
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 text-stone-500 font-mono text-xs" data-id="element-1034">
                JD
              </div>
              <div data-id="element-1035">
                <p className="text-sm text-stone-900" data-id="element-1036">
                  <span className="font-medium" data-id="element-1037">John Doe</span> (Agency) viewed
                  proposal
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-1038">
                  Today, 10:42 AM
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-1039">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600 font-mono text-xs" data-id="element-1040">
                BQ
              </div>
              <div data-id="element-1041">
                <p className="text-sm text-stone-900" data-id="element-1042">
                  <span className="font-medium" data-id="element-1043">System</span> generated
                  Good/Better/Best packages
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-1044">
                  Yesterday, 3:15 PM
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-1045">
              <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 text-stone-500 font-mono text-xs" data-id="element-1046">
                JD
              </div>
              <div data-id="element-1047">
                <p className="text-sm text-stone-900" data-id="element-1048">
                  <span className="font-medium" data-id="element-1049">John Doe</span> (Agency)
                  submitted RFP via email
                </p>
                <p className="text-xs text-stone-500 font-mono mt-1" data-id="element-1050">
                  Yesterday, 3:14 PM
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}